import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';

import { LinkFields } from './LinkFields';
import type { LinkFieldValues } from './linkPrefill';
import { useEditLink } from './useEditLink';
import { useLinkFields } from './useLinkFields';

interface EditLinkDialogProps {
  readonly linkId: string;
  /** The row as it is: `item.link.url`, `item.name`, `item.link.favicon`. */
  readonly initial: LinkFieldValues;
  readonly onClose: () => void;
}

/** "Edit link": the same fields as create, prefilled from the row. */
export function EditLinkDialog({ linkId, initial, onClose }: EditLinkDialogProps) {
  const fields = useLinkFields(initial);
  const edit = useEditLink(linkId);

  const isBusy = edit.isPending || fields.isResolving;

  // Save resolves the final URL's site icon first, so a URL changed inside
  // the lookup debounce still saves the icon the form shows.
  const save = async () => {
    if (edit.isPending) return;
    const resolved = await fields.resolveValues();
    if (!resolved) return;
    edit.mutate({ before: initial, after: resolved.values }, { onSuccess: onClose });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      isCentered
      size={{ base: 'full', md: 'xl' }}
      closeOnEsc={!isBusy}
      closeOnOverlayClick={!isBusy}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="text.primary">Edit link</ModalHeader>
        <ModalCloseButton isDisabled={isBusy} />
        <ModalBody>
          <LinkFields fields={fields} isDisabled={isBusy} onSubmit={() => void save()} />
          {edit.error && (
            <Text mt={3} fontSize="sm" color="text.error" role="alert">
              {edit.error.message}
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="primary"
            isLoading={isBusy}
            loadingText="Saving…"
            isDisabled={!fields.values}
            onClick={() => void save()}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
